import { useMemo } from 'preact/hooks';
import { graphic } from 'echarts/core';
import type { LineSeriesOption } from 'echarts/charts';
import { useTheme } from '../../contexts/ThemeContext';
import { normalizeCurrencyCode } from '../../functions/calculations/currencyNormalizer';
import { colorToRgba, resolveSeriesColor } from '../../themes/chartThemeColors';
import type { ChartSeries } from '../../types/charts';
import { EChart, type AppEChartOption } from './EChart';

interface PortfolioPerformanceChartProps {
  series: ChartSeries[];
  currencyCode?: string;
  loading?: boolean;
  className?: string;
}

interface TooltipSeriesParam {
  axisValue?: string | number;
  marker?: string;
  seriesName?: string;
  value?: unknown;
}

function formatXAxisValue(value: string | number | Date): string {
  if (value instanceof Date) return value.toISOString().slice(0, 10);
  return String(value);
}

function formatCompactValue(value: number): string {
  const absoluteValue = Math.abs(value);
  if (absoluteValue >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(1).replace('.0', '')}B`;
  if (absoluteValue >= 1_000_000) return `${(value / 1_000_000).toFixed(1).replace('.0', '')}M`;
  if (absoluteValue >= 1_000) return `${(value / 1_000).toFixed(1).replace('.0', '')}K`;
  return Math.round(value).toLocaleString();
}

function formatCurrency(value: number, currencyCode?: string): string {
  const normalizedCurrency = normalizeCurrencyCode(currencyCode);
  if (!normalizedCurrency) return value.toLocaleString(undefined, { maximumFractionDigits: 0 });

  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: normalizedCurrency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    return `${normalizedCurrency} ${value.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function readNumericValue(value: unknown): number | null {
  if (typeof value === 'number') return value;
  if (Array.isArray(value)) {
    const numericValue = value.find((item) => typeof item === 'number');
    return typeof numericValue === 'number' ? numericValue : null;
  }
  return null;
}

export function PortfolioPerformanceChart({
  series,
  currencyCode,
  loading = false,
  className = '',
}: PortfolioPerformanceChartProps) {
  const { theme } = useTheme();

  const option = useMemo<AppEChartOption>(() => {
    const xValues = Array.from(
      new Set(series.flatMap((item) => item.data.map((point) => formatXAxisValue(point.x)))),
    );

    const chartSeries: LineSeriesOption[] = series.map((item, index) => {
      const color = resolveSeriesColor(theme, item.color);
      const valueByPeriod = new Map(
        item.data.map((point) => [formatXAxisValue(point.x), point.y]),
      );
      const isArea = item.type === 'area';

      return {
        id: `${item.label}-${index}`,
        name: item.label,
        type: 'line',
        data: xValues.map((period) => valueByPeriod.get(period) ?? null),
        smooth: 0.24,
        smoothMonotone: 'x',
        connectNulls: false,
        showSymbol: false,
        symbol: 'circle',
        symbolSize: 7,
        lineStyle: {
          color,
          width: isArea ? 2.25 : 2.5,
          cap: 'round',
          join: 'round',
        },
        itemStyle: {
          color,
          borderColor: theme.colors.surface,
          borderWidth: 2,
        },
        areaStyle: isArea
          ? {
              opacity: 1,
              color: new graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: colorToRgba(color, 0.3) },
                { offset: 1, color: colorToRgba(color, 0.02) },
              ]),
            }
          : undefined,
        emphasis: {
          focus: 'series',
          lineStyle: { width: 3 },
        },
      };
    });

    return {
      animation: true,
      animationDuration: 450,
      animationEasing: 'cubicOut',
      aria: {
        enabled: true,
        description: `Portfolio performance chart showing ${series.map((item) => item.label).join(' and ')} across ${xValues.length} periods.`,
        decal: { show: false },
      },
      color: chartSeries.map((item) => String(item.lineStyle && 'color' in item.lineStyle ? item.lineStyle.color : theme.colors.primary)),
      grid: {
        top: series.length > 1 ? 42 : 28,
        right: 12,
        bottom: 8,
        left: 8,
        containLabel: true,
      },
      legend: {
        show: series.length > 1,
        top: 2,
        right: 6,
        icon: 'roundRect',
        itemWidth: 14,
        itemHeight: 3,
        itemGap: 18,
        textStyle: {
          color: theme.colors.textSecondary,
          fontFamily: theme.typography.fontFamily,
          fontSize: 11,
          fontWeight: 600,
        },
      },
      tooltip: {
        trigger: 'axis',
        confine: true,
        axisPointer: {
          type: 'line',
          lineStyle: {
            color: colorToRgba(theme.colors.textSecondary, 0.35),
            width: 1,
            type: 'dashed',
          },
        },
        backgroundColor: theme.colors.surface,
        borderColor: theme.colors.border,
        borderWidth: 1,
        padding: [10, 12],
        textStyle: {
          color: theme.colors.text,
          fontFamily: theme.typography.fontFamily,
          fontSize: 12,
        },
        extraCssText: `border-radius: ${theme.borderRadius.md}; box-shadow: ${theme.shadows.md};`,
        formatter: (rawParams: unknown) => {
          const params = (Array.isArray(rawParams) ? rawParams : [rawParams]) as TooltipSeriesParam[];
          const period = escapeHtml(String(params[0]?.axisValue ?? ''));
          const rows = params
            .map((param) => {
              const numericValue = readNumericValue(param.value);
              if (numericValue === null) return '';
              return `<div class="echart-tooltip__row">${param.marker ?? ''}<span>${escapeHtml(param.seriesName ?? '')}</span><strong>${escapeHtml(formatCurrency(numericValue, currencyCode))}</strong></div>`;
            })
            .join('');
          return `<div class="echart-tooltip"><div class="echart-tooltip__title">${period}</div>${rows}</div>`;
        },
      },
      xAxis: {
        type: 'category',
        data: xValues,
        boundaryGap: false,
        axisLine: {
          show: true,
          lineStyle: { color: colorToRgba(theme.colors.border, 0.8) },
        },
        axisTick: { show: false },
        axisLabel: {
          color: theme.colors.textSecondary,
          fontFamily: theme.typography.fontFamily,
          fontSize: 10,
          hideOverlap: true,
          margin: 10,
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        scale: false,
        min: (range: { min: number }) => (range.min >= 0 ? 0 : undefined),
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: theme.colors.textSecondary,
          fontFamily: theme.typography.fontFamily,
          fontSize: 10,
          formatter: (value: number) => formatCompactValue(value),
          margin: 10,
        },
        splitNumber: 4,
        splitLine: {
          show: true,
          lineStyle: {
            color: colorToRgba(theme.colors.border, 0.58),
            width: 1,
          },
        },
      },
      series: chartSeries,
    };
  }, [series, currencyCode, theme]);

  const hasData = series.some((item) => item.data.length > 0);

  return (
    <EChart
      className={`portfolio-performance-echart ${className}`.trim()}
      option={option}
      ariaLabel="Portfolio performance over the selected period"
      loading={loading}
      empty={!loading && !hasData}
      emptyMessage="No performance data available"
    />
  );
}
